/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */

import {ViewEncapsulation} from '@angular/core';

import {CompileDirectiveMetadata, CompileIdentifierMetadata, CompileStylesheetMetadata, identifierModuleUrl, identifierName} from './compile_metadata';
import {CompilerInjectable} from './injectable';
import * as o from './output/output_ast';
import {ShadowCss} from './shadow_css';
import {UrlResolver} from './url_resolver';

const /** @type {?} */ COMPONENT_VARIABLE = '%COMP%';
const /** @type {?} */ HOST_ATTR = `_nghost-${COMPONENT_VARIABLE}`;
const /** @type {?} */ CONTENT_ATTR = `_ngcontent-${COMPONENT_VARIABLE}`;

export class StylesCompileDependency {
/**
 * @param {?} name
 * @param {?} moduleUrl
 * @param {?} isShimmed
 * @param {?} valuePlaceholder
 */
constructor(
public name: string,
public moduleUrl: string,
public isShimmed: boolean,
public valuePlaceholder: CompileIdentifierMetadata) {}
}

function StylesCompileDependency_tsickle_Closure_declarations() {
 /** @type {?} */
StylesCompileDependency.prototype.name;
 /** @type {?} */
StylesCompileDependency.prototype.moduleUrl;
 /** @type {?} */
StylesCompileDependency.prototype.isShimmed;
 /** @type {?} */
StylesCompileDependency.prototype.valuePlaceholder;
}


export class StylesCompileResult {
/**
 * @param {?} componentStylesheet
 * @param {?} externalStylesheets
 */
constructor(
public componentStylesheet: CompiledStylesheet,
public externalStylesheets: CompiledStylesheet[]) {}
}

function StylesCompileResult_tsickle_Closure_declarations() {
 /** @type {?} */
StylesCompileResult.prototype.componentStylesheet;
 /** @type {?} */
StylesCompileResult.prototype.externalStylesheets;
}


export class CompiledStylesheet {
/**
 * @param {?} statements
 * @param {?} stylesVar
 * @param {?} dependencies
 * @param {?} isShimmed
 * @param {?} meta
 */
constructor(
public statements: o.Statement[],
public stylesVar: string,
public dependencies: StylesCompileDependency[],
public isShimmed: boolean,
public meta: CompileStylesheetMetadata) {}
}

function CompiledStylesheet_tsickle_Closure_declarations() {
 /** @type {?} */
CompiledStylesheet.prototype.statements;
 /** @type {?} */
CompiledStylesheet.prototype.stylesVar;
 /** @type {?} */
CompiledStylesheet.prototype.dependencies;
 /** @type {?} */
CompiledStylesheet.prototype.isShimmed;
 /** @type {?} */
CompiledStylesheet.prototype.meta;
}


@CompilerInjectable()
export class StyleCompiler {
private _shadowCss: ShadowCss = new ShadowCss();
/**
 * @param {?} _urlResolver
 */
constructor(private _urlResolver: UrlResolver) {}
/**
 * @param {?} comp
 * @return {?}
 */
compileComponent(comp: CompileDirectiveMetadata): StylesCompileResult {
    const /** @type {?} */ externalStylesheets: CompiledStylesheet[] = [];
    const /** @type {?} */ componentStylesheet: CompiledStylesheet = this._compileStyles(
        comp, new CompileStylesheetMetadata({
          styles: comp.template.styles,
          styleUrls: comp.template.styleUrls,
          moduleUrl: identifierModuleUrl(comp.type)
        }),
        true);
    comp.template.externalStylesheets.forEach((stylesheetMeta) => {
      const /** @type {?} */ compiledStylesheet = this._compileStyles(comp, stylesheetMeta, false);
      externalStylesheets.push(compiledStylesheet);
    });
    return new StylesCompileResult(componentStylesheet, externalStylesheets);
  }
/**
 * @param {?} comp
 * @param {?} stylesheet
 * @param {?} isComponentStylesheet
 * @return {?}
 */
private _compileStyles(
      comp: CompileDirectiveMetadata, stylesheet: CompileStylesheetMetadata,
      isComponentStylesheet: boolean): CompiledStylesheet {
    const /** @type {?} */ shim = comp.template.encapsulation === ViewEncapsulation.Emulated;
    const /** @type {?} */ styleExpressions =
        stylesheet.styles.map(plainStyle => o.literal(this._shimIfNeeded(plainStyle, shim)));
    const /** @type {?} */ dependencies: StylesCompileDependency[] = [];
    for (let /** @type {?} */ i = 0; i < stylesheet.styleUrls.length; i++) {
      const /** @type {?} */ identifier: CompileIdentifierMetadata = {reference: null};
      dependencies.push(new StylesCompileDependency(
          getStylesVarName(null), stylesheet.styleUrls[i], shim, identifier));
      styleExpressions.push(new o.ExternalExpr(identifier));
    }
    // styles variable contains plain strings and arrays of other styles arrays (recursive),
    // so we set its type to dynamic.
    const /** @type {?} */ stylesVar = getStylesVarName(isComponentStylesheet ? comp : null);
    const /** @type {?} */ stmt = o.variable(stylesVar)
                     .set(o.literalArr(
                         styleExpressions, new o.ArrayType(o.DYNAMIC_TYPE, [o.TypeModifier.Const])))
                     .toDeclStmt(null, [o.StmtModifier.Final]);
    return new CompiledStylesheet([stmt], stylesVar, dependencies, shim, stylesheet);
  }
/**
 * @param {?} style
 * @param {?} shim
 * @return {?}
 */
private _shimIfNeeded(style: string, shim: boolean): string {
    return shim ? this._shadowCss.shimCssText(style, CONTENT_ATTR, HOST_ATTR) : style;
  }
}

function StyleCompiler_tsickle_Closure_declarations() {
 /** @type {?} */
StyleCompiler.prototype._shadowCss;
 /** @type {?} */
StyleCompiler.prototype._urlResolver;
}

/**
 * @param {?} component
 * @return {?}
 */
function getStylesVarName(component: CompileDirectiveMetadata): string {
  let /** @type {?} */ result = `styles`;
  if (component) {
    result += `_${identifierName(component.type)}`;
  }
  return result;
}

interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
